import { memo, useCallback, useMemo, useState } from 'react';
import { APPROVAL_STATES } from '../../../../CONSTANTS';
import { ApprovalState } from '../../../../../../../../api/generated/data-contracts';
import {
    DefaultButton,
    Dialog,
    DialogFooter,
    Dropdown,
    IDropdownOption,
    IDropdownProps,
    IRenderFunction,
    Icon,
    PrimaryButton,
    Stack,
    Text,
    mergeStyleSets,
    useTheme,
    getTheme,
    TooltipHost,
} from '@fluentui/react';
import { ApiCalls } from '../../../../../../../../api/api';
import { useMutation } from '@tanstack/react-query';
import { useRowMetaContext } from '../../../../context/RowMetaContext';
import { TimeEntryApprovalDtoState, useApprovalState, useMissingApprovalCount, useActivityState, useRateState } from '../../../../../../../../_grid/grid3';
import { getApprovalStateBackground } from '../../../../../../../../_helpers/getApprovalStateBackground';
import { useTranslation } from 'react-i18next';
import { useConfigValues } from '../../../../../../../../hooks/queries/useConfigValues';

const stateOptions = APPROVAL_STATES.map((state): IDropdownOption<ApprovalState> => {
    return {
        key: state,
        text: state,
        data: state,
    };
});

const getStateOptionsWithDisabled = (rateMissing: boolean) =>
    APPROVAL_STATES.map(
        (state): IDropdownOption<ApprovalState> => ({
            key: state,
            text: state,
            data: state,
            disabled: state === 'Approved' && rateMissing,
            title: state === 'Approved' && rateMissing ? 'A rate must be assigned before approving' : undefined,
        }),
    );

export const StateDropdown = memo(function StateDropdown() {
    const {
        data: {
            configuration: { lockProcessedTimeEntries, approval: approvalConfig },
        },
    } = useConfigValues();
    const { t } = useTranslation('timesheet');
    const { row, parentRows } = useRowMetaContext<TimeEntryApprovalDtoState>();
    const [approval, setApproval] = useApprovalState(row.id);
    const [activity] = useActivityState(row.id);
    const [currentRate] = useRateState(row.id);
    const enforceRate = approvalConfig?.projectManagerApproval?.enforceRateBeforeApproval;
    const hasActivity = activity != null;
    const rateMissing = enforceRate && !hasActivity && (currentRate == null || currentRate === undefined);
    const { mutate: mutateApproval } = useMutation((selectedApprovalState: ApprovalState) =>
        ApiCalls.setApprovalState({ selectedApprovalState, timeEntryId: row.timeEntry.id }),
    );
    const missingApprovalCount = useMissingApprovalCount(row.id);
    const [showProjectClosureWarning, setShowProjectClosureWarning] = useState(false);

    const onChange = useCallback(
        (event: any, option?: IDropdownOption<ApprovalState>) => {
            if (option?.data && option.data !== approval) {
                if (option.data === 'Approved' && rateMissing) {
                    return; // Block approve when rate is missing and enforcement is on
                }
                if (option.data === 'Approved' && missingApprovalCount === 1) {
                    // Warn about project closure
                    setShowProjectClosureWarning(true);
                } else {
                    setApproval(option.data);
                    mutateApproval(option.data);
                }
            }
        },
        [approval, missingApprovalCount, mutateApproval, rateMissing, setApproval],
    );

    const theme = useTheme();

    const approvalBackground = useMemo((): IDropdownProps['styles'] => {
        return {
            title: {
                backgroundColor: getApprovalStateBackground(approval, theme),
                color: '#ffffff !important',
                borderColor: getApprovalStateBackground(approval, theme),
            },
            caretDown: { color: '#ffffff !important' },
        };
    }, [approval, theme]);

    const onRenderCaretDown: IRenderFunction<IDropdownProps> = useCallback(
        (props, defaultRender) => {
            if (missingApprovalCount === 0) {
                return null;
            }
            return defaultRender(props);
        },
        [missingApprovalCount],
    );

    const externallyApprovedMessage = useMemo(() => {
        if (row.externalId && lockProcessedTimeEntries) {
            return t('readOnlyMessages.externallyLocked');
        }
    }, [lockProcessedTimeEntries, row.externalId, t]);

    const isLocked = useMemo(() => {
        // Locked if externally approved OR (all approvals are done AND activity doesn't allow edit after approval)
        return Boolean(externallyApprovedMessage) || (missingApprovalCount === 0 && !activity?.allowEditAfterApproval);
    }, [externallyApprovedMessage, missingApprovalCount, activity?.allowEditAfterApproval]);

    const resolvedOptions = useMemo(() => (rateMissing ? getStateOptionsWithDisabled(true) : stateOptions), [rateMissing]);

    const tooltipContent = useMemo(() => {
        if (externallyApprovedMessage) return externallyApprovedMessage;
        if (rateMissing) return 'A rate must be assigned before approving';
        return undefined;
    }, [externallyApprovedMessage, rateMissing]);

    return (
        <>
            <TooltipHost content={tooltipContent}>
                <Dropdown
                    //
                    disabled={isLocked}
                    options={resolvedOptions}
                    onChange={onChange}
                    selectedKey={approval}
                    styles={approvalBackground}
                    onRenderCaretDown={onRenderCaretDown}
                />
            </TooltipHost>
            <Dialog minWidth={400} hidden={!showProjectClosureWarning} dialogContentProps={{ title: 'Approve and close time registration?' }}>
                After all time registrations for this month are approved, you won’t be able to change the approvals for the project{' '}
                <strong>"{parentRows.at(-1).name}"</strong> unless the person who submitted the timesheet retracts and resubmits, or the activity allows editing
                after approval.
                <DialogFooter>
                    <DefaultButton
                        onClick={() => {
                            setShowProjectClosureWarning(false);
                        }}
                    >
                        Cancel
                    </DefaultButton>
                    <PrimaryButton
                        onClick={() => {
                            setShowProjectClosureWarning(false);
                            setApproval('Approved');
                            mutateApproval('Approved');
                        }}
                        className={styles.approveButton}
                    >
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                            <Text className={styles.approveButtonText}>Approve</Text>
                            <Icon iconName="CheckMark" />
                        </Stack>
                    </PrimaryButton>
                </DialogFooter>
            </Dialog>
        </>
    );
});

const theme = getTheme();
const styles = mergeStyleSets({
    approveButton: {
        backgroundColor: theme.semanticColors.successIcon,
        selectors: {
            ':hover': { backgroundColor: 'rgb(16,100,16)' },
        },
    },
    approveButtonText: { color: theme.semanticColors.accentButtonText },
});
